import { config } from '@config/config';
import { pipe } from 'rxjs';
import { map } from 'rxjs/operators';

import { UrlUtils } from '@app/utils/url.utils';

export class Page {
    /**
     * These four fields define a page on the API
     */
    private _page: number;
    private _pageSize: number;
    private _sortField: string;
    private _sortDirection: 'ASC' | 'DESC';

    /**
     * Defines a specific range which is paginated. If not
     * specified, pagination is over all existing values.
     */
    private _upper: string | number;
    private _lower: string | number;

    /**
     * ONLY USED BY CACHE
     * The cursor field is stored in the cache and used for
     * getting a page out of the cache. This is needed
     * because an offset cannot be used when some pages are
     * not stored in the cache and some are.
     */
    private _cursor: string | number;

    constructor(
        page?: number,
        pageSize?: number,
        sortField?: string,
        sortDirection?: 'ASC' | 'DESC',
        upper?: string | number,
        lower?: string | number
    ) {
        this._page = Math.floor(page) || 1;
        this._pageSize = Math.floor(pageSize) || 20;
        this._sortField = sortField || 'id';
        this._sortDirection = sortDirection || 'DESC';
        this._upper = upper;
        this._lower = lower;
        this.validate();
    }

    static fromUrl(url: string): Page {
        const params = UrlUtils.getUrlParams(url);
        const page = new Page();
        if (params.limit && params.offset) {
            page._page = Math.floor(Number(params.offset) / Number(params.limit)) + 1;
            page._pageSize = Math.floor(Number(params.limit));
        }
        page._sortField = params.sortField || 'id';
        page._sortDirection = (params.sortDirection || 'DESC') as 'ASC' | 'DESC';

        return page;
    }

    static sort(sortField: string, sortDirection: 'ASC' | 'DESC') {
        return pipe(
            map((models: Array<any>) =>
                models.sort((a, b) => {
                    if (sortDirection === 'ASC') {
                        return a[sortField] < b[sortField] ? -1 : 1;
                    } else {
                        return a[sortField] > b[sortField] ? -1 : 1;
                    }
                })
            )
        );
    }

    static deserialize(input: string) {
        const params = input.split('|');
        const page = new Page(Number(params[0]), Number(params[1]), params[2], params[3] as 'ASC' | 'DESC');
        page.setBounds(params[4], params[5]);
        page._cursor = params[6];

        return page;
    }

    serialize() {
        const arr = [
            this._page,
            this._pageSize,
            this._sortField,
            this._sortDirection,
            this._upper,
            this._lower,
            this._cursor,
        ];

        return arr.join('|');
    }

    get page() {
        return this._page;
    }

    set page(p) {
        this._page = Math.floor(p) || 1;
    }

    get pageSize() {
        return this._pageSize;
    }

    set pageSize(s) {
        this._pageSize = Math.floor(s) || 20;
    }

    get sortField() {
        return this._sortField;
    }

    set sortField(f) {
        this._sortField = f;
    }

    get sortDirection() {
        return this._sortDirection;
    }

    set sortDirection(d) {
        this._sortDirection = d;
    }

    get limit() {
        return Math.floor(this._pageSize);
    }

    set limit(l) {
        this._pageSize = Math.floor(l);
        if (this._pageSize < 1) {
            this._pageSize = 1;
        }
    }

    get offset() {
        if (this._pageSize === Infinity) {
            return 0;
        }

        return Math.floor((this._page - 1) * this._pageSize);
    }

    set offset(o) {
        if (this._pageSize !== Infinity) {
            this._page = Math.floor((Number(o) || 0) / this._pageSize) + 1;
        }
    }

    next() {
        this._page++;
    }

    prev() {
        this._page--;
        if (this._page < 1) {
            this._page = 1;
        }
    }

    setBounds(upper: string | number, lower: string | number) {
        this._upper = upper;
        this._lower = lower;
    }

    get cursor() {
        return this._cursor;
    }

    set cursor(c) {
        this._cursor = c;
    }

    sort() {
        return pipe(
            map((models: Array<any>) =>
                models.sort((a, b) => {
                    if (this._sortDirection === 'ASC') {
                        if (a[this._sortField] < b[this._sortField]) {
                            return -1;
                        } else if (a[this._sortField] > b[this._sortField]) {
                            return 1;
                        } else {
                            return 0;
                        }
                    } else {
                        if (a[this._sortField] > b[this._sortField]) {
                            return -1;
                        } else if (a[this._sortField] < b[this._sortField]) {
                            return 1;
                        } else {
                            return 0;
                        }
                    }
                })
            )
        );
    }

    /**
     * Returns true if this page is equal to or contained within the
     * input Page, otherwise false
     * @param other The page to compare to
     */
    compare(other: Page) {
        if (
            (!this._upper && other._upper) ||
            (this._upper && !other._upper) ||
            (this._upper && other._upper && this._upper !== other._upper)
        ) {
            return false;
        }
        if (
            (!this._lower && other._lower) ||
            (this._lower && !other._lower) ||
            (this._lower && other._lower && this._lower !== other._lower)
        ) {
            return false;
        }

        const minItem = this.offset;
        const maxItem = this.offset + this.limit;
        const otherMinItem = other.offset;
        const otherMaxItem = other.offset + other.limit;

        return (
            minItem >= otherMinItem &&
            maxItem <= otherMaxItem &&
            this._sortField === other._sortField &&
            this._sortDirection === other._sortDirection
        );
    }

    private validate() {
        if (this._page < 1) {
            this._page = 1;
        }

        if (this._pageSize !== Infinity && this._pageSize > config.maxPageSize) {
            this._pageSize = config.maxPageSize;
        }

        if (this._pageSize === Infinity && this._page !== 1) {
            throw new Error('Page must be set to 1 if the page size is Infinity');
        }
    }
}
